import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CarOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { shippingCompanyAPI } from '../api';

const { Title, Text } = Typography;

const ShippingCompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await shippingCompanyAPI.getAll();
      setCompanies(res.data || []);
    } catch (error) {
      message.error('获取物流公司列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCompany(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCompany(record);
    form.setFieldsValue({
      name: record.name,
      tracking_url_template: record.tracking_url_template,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await shippingCompanyAPI.delete(id);
      message.success('删除成功');
      fetchCompanies();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('删除失败');
      }
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingCompany) {
        await shippingCompanyAPI.update(editingCompany.id, values);
        message.success('更新成功');
      } else {
        await shippingCompanyAPI.create(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      setEditingCompany(null);
      form.resetFields();
      fetchCompanies();
    } catch (error) {
      if (error?.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else if (!error?.errorFields) {
        // 忽略校验错误
        message.error(editingCompany ? '更新失败' : '创建失败');
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
    },
    {
      title: '物流公司',
      dataIndex: 'name',
      key: 'name',
      width: '22%',
      render: (value) => (
        <Tag icon={<CarOutlined />} color="blue">
          {value}
        </Tag>
      ),
    },
    {
      title: '查询URL模板',
      dataIndex: 'tracking_url_template',
      key: 'tracking_url_template',
      width: '40%',
      ellipsis: true,
      render: (value) =>
        value ? (
          <Space size="small">
            <LinkOutlined />
            <Text>{value}</Text>
          </Space>
        ) : (
          <Text type="secondary">未设置</Text>
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      render: (time) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '15%',
      render: (time) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个物流公司吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <CarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              物流公司管理
            </Title>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建物流公司
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingCompany ? '编辑物流公司' : '新建物流公司'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          setEditingCompany(null);
          form.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="物流公司名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="如：燕文，四方（这个给自己看的，客户看不到）" />
          </Form.Item>
          <Form.Item
            name="tracking_url_template"
            label="查询URL模板"
            extra="使用 __TRACKING__NUMBER__ 作为跟踪号占位符，例如 https://track.com/?code=__TRACKING__NUMBER__"
          >
            <Input placeholder="https://example.com/track/__TRACKING__NUMBER__" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShippingCompanyManagement;
